<!-- The project choice list: PROJECTS header, one row per project (colour dot, name, counts, check mark on the active one),
     "All projects" with at least two, then "Manage projects" (emits manage: the wrapper closes itself and opens the
     Projects dialog). Shown in the project menu at the top right (TopControls.vue, #projectMenu) and in the Profiles
     window (ProfilesDialog.vue, #profileProjects: on the phone the only place to choose the project). Draws nothing
     before a profile is active or when the profile has no projects. -->
<script setup lang="ts">
import { computed } from 'vue'
import { useDataStore } from '../stores/data'
import { useProjectsStore } from '../stores/projects'
import { useUiStore } from '../stores/ui'
import { useI18n } from '../composables/useI18n'
import { ALL_PROJECTS, projCounts } from '../features/projects'
import { safeColor } from '../utils/color'

const emit = defineEmits<{ manage: [] }>()
const store = useDataStore()
const projects = useProjectsStore()
const ui = useUiStore()
const { t } = useI18n()

// A new list at every save (store.rev) and refresh (ui.tick): the data is changed in place
const view = computed(() => {
  void store.rev; void ui.tick
  if (!store.currentProfile()) return null
  const d = store.getData()
  if (!d.projects.length) return null
  const list = d.projects.map(p => ({ id: p.id, label: p.name, sub: projCounts(d, p.id), color: safeColor(p.color) as string | null }))
  if (d.projects.length > 1) list.push({ id: ALL_PROJECTS, label: t('All projects'), sub: t('See everything together, with coloured labels'), color: null })
  return { list, active: projects.active() }
})
</script>

<template>
  <template v-if="view">
    <div class="px-3 pt-1 pb-2 text-[11px] font-bold text-gray-500 tracking-wider">{{ t('PROJECTS') }}</div>
    <button v-for="r in view.list" :key="r.id" type="button" :class="['w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl', view.active === r.id ? 'bg-brand/15' : 'hover:bg-darkbtn']"
      @click="projects.choose(r.id)">
      <span v-if="r.color" class="w-3 h-3 rounded-full inline-block flex-shrink-0" :style="{ background: r.color }"></span>
      <i v-else class="fas fa-layer-group text-brand w-3 text-center text-xs"></i>
      <span class="flex-1 min-w-0">
        <span :class="['block text-sm font-semibold truncate', view.active === r.id ? 'text-brand' : 'text-gray-100']">{{ r.label }}</span>
        <span class="block text-[11px] text-gray-500 truncate">{{ r.sub }}</span>
      </span>
      <i v-if="view.active === r.id" class="fas fa-check text-brand text-xs"></i>
    </button>
    <div class="border-t border-gray-800 my-1.5"></div>
    <button type="button" class="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-darkbtn text-sm text-gray-300" @click="emit('manage')">
      <i class="fas fa-sliders text-brand w-3 text-center text-xs"></i>{{ t('Manage projects') }}
    </button>
  </template>
</template>
